import { Component, Input, OnChanges } from '@angular/core';
import {
	AggregateAssetResult,
	Filter,
	LocalizedTextIds,
	LocPrefix,
	Mode,
	VisualizationType,
} from 'company-finder-common';
import {
	ApplicationContext,
	BrandColors,
	ComponentBase,
	DeploymentContext,
	SearchService,
	WebAnalyticsService,
} from '@Common';
import { LocationIndicatorComponent } from './components/location-indicator/location-indicator.component.js';
import _ from 'lodash-es';

import { LocationGroup, LocationInfo } from '@Common';
import { Summary } from '@Common';
import { Location } from 'company-finder-common';
import { TooltipHandler } from './models/tooltip-handler.js';

@Component({
	selector: 'explore',
	templateUrl: './explore.component.html',
	styleUrls: ['./explore.component.scss'],
	standalone: false,
})
export class ExploreComponent extends ComponentBase implements OnChanges {
	public static readonly SMALL_INDICATOR_THRESHOLD: number = 1 / 3;
	public static readonly MEDIUM_INDICATOR_THRESHOLD: number = 2 / 3;
	public locationInfos = new LocationGroup();
	public virtualLocationInfo: LocationGroup;
	@Input()
	public summary: Summary;
	@Input()
	public assetSummary: AggregateAssetResult;

	public get taStageCounts() {
		return this.assetSummary?.taStageCounts ?? [];
	}
	public get focusAreaCounts() {
		return this.assetSummary?.focusAreaCounts ?? [];
	}

	public get assetsLabel(): string {
		return this.Loc(LocalizedTextIds.Assets);
	}

	public get title() {
		// Asset explore requires exactly one sector to be selected at all times.
		// Include it in the label.
		const modeLabel = this.inCompanyMode
			? this.modePluralName
			: `${this.searchService.assetFilter.primarySectors[0]} ${this.modePluralName}`;
		return this.Loc(LocalizedTextIds.ExploreCompanies, modeLabel);
	}
	public VisualizationTypes = VisualizationType;

	private companyVisualizationType: VisualizationType;
	private assetVisualizationType: VisualizationType;

	public get visualizationType(): VisualizationType {
		switch (this.mode) {
			case Mode.Asset:
				return (
					this.assetVisualizationType ??
					VisualizationType.TherapeuticArea
				);
			case Mode.Company:
			default:
				return (
					this.companyVisualizationType ?? VisualizationType.Geography
				);
		}
	}

	public set visualizationType(value: VisualizationType) {
		switch (this.mode) {
			case Mode.Asset:
				this.assetVisualizationType = value;
				break;
			case Mode.Company:
			default:
				this.companyVisualizationType = value;
				break;
		}
	}

	public tooltipHandler: TooltipHandler;
	public renderedGeo: boolean = false;

	public constructor(
		dc: DeploymentContext,
		private appContext: ApplicationContext,
		private webAnalyticsService: WebAnalyticsService,
		private searchService: SearchService
	) {
		super(dc);

		if (this.inCompanyMode) {
			this.visualizationType =
				this.appContext.activeVisualizationType ||
				this._deploymentContext.initialVisualization;
		} else {
			this.visualizationType = VisualizationType.TherapeuticArea;
		}

		if (this.visualizationType === VisualizationType.Geography) {
			this.renderedGeo = true;
		}
		this.tooltipHandler = {
			locationIndicator: null,
			left: 0,
			bottom: 0,
			markerElement: null,
			show: (locationIndicator: LocationIndicatorComponent) => {
				this.registerWindowHandlerSubscription('click', (_ev) =>
					this.tooltipHandler.hide()
				);
				this.tooltipHandler.locationIndicator = locationIndicator;
			},
			hide: () => {
				this.tooltipHandler.locationIndicator = undefined;
				this.unregisterWindowHandlerSubscription('click');
			},
			reposition: () => {
				const locIndicator = this.tooltipHandler.locationIndicator;
				if (locIndicator) {
					locIndicator.positionTooltip();
				}
			},
		};
	}

	public get isSector(): boolean {
		return this.visualizationType === VisualizationType.Sector;
	}

	public get isTags(): boolean {
		return this.visualizationType === VisualizationType.Tags;
	}

	public get sectorsEnabled() {
		return this.featureSwitches.enableSectors && this.inCompanyMode;
	}

	public get tagsEnabled() {
		return this.featureSwitches.enableShowTags && this.inCompanyMode;
	}

	public get geoEnabled() {
		return (
			this.featureSwitches.enableShowGeoLocationMap && this.inCompanyMode
		);
	}

	public isSelected(vis: VisualizationType) {
		return this.visualizationType === vis;
	}

	public get options() {
		switch (this.mode) {
			case Mode.Company:
				return this.companyOptions;
			case Mode.Asset:
				return this.assetOptions;
		}
	}

	public get assetOptions() {
		return [
			{
				type: VisualizationType.FocusArea,
				label: this.FocusAreaLabel,
			},
			{
				type: VisualizationType.TherapeuticArea,
				label: this.TherapeuticAreaLabel,
			},
		];
	}

	public get companyOptions() {
		const options = [];

		if (this.sectorsEnabled) {
			options.push({
				type: VisualizationType.Sector,
				label: this.Loc(LocalizedTextIds.Sector),
			});
		}

		if (this.tagsEnabled) {
			options.push({
				type: VisualizationType.Tags,
				label: this.Loc(LocalizedTextIds.Tags),
			});
		}

		if (this.geoEnabled) {
			options.push({
				type: VisualizationType.Geography,
				label: this.Loc(LocalizedTextIds.Geography),
			});
		}

		return options;
	}

	public get isGeography(): boolean {
		return this.visualizationType === VisualizationType.Geography;
	}

	public get isFocusArea(): boolean {
		return this.visualizationType === VisualizationType.FocusArea;
	}

	public get isDevelopmentStage(): boolean {
		return this.visualizationType === VisualizationType.TherapeuticArea;
	}

	public get tooltipStyle(): Partial<CSSStyleDeclaration> {
		return {
			left: `${this.tooltipHandler.left}px`,
			bottom: `${this.tooltipHandler.bottom}px`,
		};
	}

	public get showVirtualLoc() {
		return this.isGeography && this.virtualLocationInfo?.length > 0;
	}

	public get showTooltip() {
		return this.featureSwitches.enableShowGeoLocationMap && this.loc;
	}

	public get loc() {
		return this.tooltipHandler?.locationIndicator?.locationMarker;
	}

	public get tooltipColors() {
		return this.tooltipHandler.locationIndicator.colors;
	}

	public get tooltipSectors() {
		return this.tooltipHandler.locationIndicator.sectors
			.map((sector, index) => {
				return {
					display: this.loc.counts[index] > 0,
					style: {
						backgroundColor: this.tooltipColors[index],
					},
					value: this.loc.counts[index],
					name: sector,
				};
			})
			.filter((sector) => sector.display);
	}

	public get sectorAndStyles() {
		return this._deploymentContext.sectorNames.map((sector, index) => {
			return {
				sector: this.sectorShortName(sector),
				style: {
					backgroundColor: this.themeSettings.sectorColors[index],
				},
			};
		});
	}

	public async ngOnChanges(): Promise<void> {
		// Note: When entering this component from a different route, ngOnChanges is called with an undefined Summary
		//  then ngOnInit, and then ngOnChanges is called as a result of having a Summary object provided from a service call.
		//  When toggling between Explore and Search Results, going back to Explore triggers ngOnChanges WITH Summary
		//  data prior to the ngOnInit call, and then not again.  So, don't attempt to initialize variables with ngOninit that might
		//  otherwise step over the previous data manipultion that might have already been done in ngOnChanges.
		if (!this.summary) {
			return;
		}

		// Reset the markers prior to calculations
		this.locationInfos = new LocationGroup();
		this.virtualLocationInfo = new LocationGroup();

		this.summary.locations
			.map((location) => this.getLocationInfo(location))
			.forEach((info) => {
				if (!info.location.excludeFromMap) {
					this.locationInfos.push(info);
				} else {
					this.virtualLocationInfo.push(info);
				}
			});
	}

	public sectorShortName(sector: string): string {
		return this.LocWithPrefix(sector, LocPrefix.SectorShortName);
	}

	private get sectorsAreConfigured(): boolean {
		return this._deploymentContext.sectorNames.length > 0;
	}

	private getLocationInfo(location: Location): LocationInfo {
		const sectors = this.sectorsAreConfigured
			? this._deploymentContext.sectorNames
			: [null]; // If no sectors, group all into one "sector" with no name

		const sectorCounts = sectors.map(
			(sector) =>
				this.summary.numberOfCompaniesAtLocationBySector(
					location?.name,
					sector
				) || 0
		);
		return {
			location,
			sectorCounts: this.sectorsAreConfigured
				? sectorCounts
				: [_.sum(sectorCounts)],
		};
	}

	public setVisualizationType(visualizationType: VisualizationType): void {
		if (
			!this.renderedGeo &&
			visualizationType === VisualizationType.Geography
		) {
			this.renderedGeo = true;
		}
		this.visualizationType = this.appContext.activeVisualizationType =
			visualizationType;
		this.webAnalyticsService.trackEvent('select-visualization', {
			label: this.visualizationType,
		});
	}

	public selectFocusAndDevStage(selection: {
		category: string;
		group: string;
	}) {
		this.searchService.filter = new Filter({
			...Filter.emptyFilter,
			primarySectors: this.searchService.filter.primarySectors,
			focusAreas: [selection.category],
			developmentStages: [selection.group],
		});
		this.searchService.navigateToSearchResults();
	}

	public selectTAAndDevStage(selection: { category: string; group: string }) {
		this.searchService.filter = new Filter({
			...Filter.emptyFilter,
			primarySectors: this.searchService.filter.primarySectors,
			therapeuticAreas: [selection.category],
			developmentStages: [selection.group],
		});
		this.searchService.navigateToSearchResults();
	}

	public get DevelopmentStageLabel(): string {
		return this.Loc(LocalizedTextIds.DevelopmentStage);
	}

	public get FocusAreaLabel(): string {
		return this.Loc(LocalizedTextIds.FocusArea);
	}

	public get TherapeuticAreaLabel(): string {
		return this.Loc(LocalizedTextIds.TherapeuticArea);
	}

	public get TherapeuticAreaTitle(): string {
		return this.Loc(
			LocalizedTextIds.RandDChartTitle,
			this.DevelopmentStageLabel,
			this.TherapeuticAreaLabel
		);
	}

	public get FocusAreaTitle(): string {
		return this.Loc(
			LocalizedTextIds.RandDChartTitle,
			this.DevelopmentStageLabel,
			this.FocusAreaLabel
		);
	}

	public get developmentStages() {
		return (
			this._deploymentContext.referenceValueData.developmentStages[
				this.searchService.assetFilter.primarySectors[0]
			]?.map((stage) => stage.value) ?? []
		);
	}

	private static devStageColorMap = [
		{ category: 'Lead (H2L-Lo)', color: BrandColors.Accent11 },
		{
			category: 'Preclinical (GLP Tox-IND)',
			color: BrandColors.Accent12,
		},
		{ category: 'Phase 1', color: BrandColors.Accent07 },
		{ category: 'Phase 2', color: BrandColors.Accent08 },
		{ category: 'Phase 3', color: BrandColors.Accent09 },
		{ category: 'Marketed Products', color: BrandColors.Maroon05 },
		{ category: 'Various', color: BrandColors.WarmGray2 }, // Not specified by Kenneth
		{ category: 'Biomarkers', color: BrandColors.Accent05 }, // Not specified by Kenneth
		{
			category: 'Discovery (Research, TI/TV)',
			color: BrandColors.Accent10,
		},
		{ category: 'Commercial', color: BrandColors.Maroon05 },
		{ category: 'Pre-Commercial', color: BrandColors.Accent08 },
		{ category: 'No Information', color: BrandColors.WarmGray2 },
		{ category: 'Undisclosed', color: BrandColors.WarmGray2 },
		{ category: 'N/A', color: BrandColors.WarmGray2 },
	];

	public getColorFromDevStage(category: string): string {
		return (
			ExploreComponent.devStageColorMap.find(
				(entry) => entry.category === category
			)?.color ?? BrandColors.Accent01
		);
	}
}
